import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Button,
  TextField,
  InputLabel,
  Typography,
  FormControl,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import useTitle from '../hooks/useTitle';
import { createErrorNotification } from '../utils/createErrorNotification';
import statusCodes, { errorDetails } from '../utils/statusCodes';
import { register } from '../services/auth';

const SignUpView = () => {
  const [values, setValues] = useState({
    username: '',
    password: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { t } = useTranslation('promptbar');

  const navigate = useNavigate();

  const title = t('promptbar:signup');
  const pageTitle = t('promptbar:pageTitle', {
    title,
  });

  useTitle(pageTitle);

  const signupSchema = Yup.object({
    username: Yup.string()
      .min(3, t('usernameMin'))
      .max(50, t('usernameMax'))
      .matches(/^[a-zA-Z0-9._-]+$/, t('usernameFormat'))
      .required(t('usernameRequired')),

    password: Yup.string()
      .required(t('passwordRequired'))
      .min(8, t('passwordMin'))
      .matches(/^[\x21-\x7E]+$/, t('passwordLatinOnly'))
      .matches(/[A-Z]/, t('passwordUppercase'))
      .matches(/[a-z]/, t('passwordLowercase'))
      .matches(/\d/, t('passwordNumber'))
      .matches(/[^A-Za-z\d]/, t('passwordSpecial')),

    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], t('passwordsMismatch'))
      .required(t('confirmPasswordRequired')),
  });

  const handleChange = async evt => {
    const { name, value } = evt.target;

    const newValues = { ...values, [name]: value };
    setValues(newValues);

    try {
      await signupSchema.validateAt(name, newValues);
      setErrors(prev => ({ ...prev, [name]: '' }));
    } catch (err) {
      setErrors(prev => ({ ...prev, [name]: err.message }));
    }

    if (name === 'password' && newValues.confirmPassword) {
      try {
        await signupSchema.validateAt('confirmPassword', newValues);
        setErrors(prev => ({ ...prev, confirmPassword: '' }));
      } catch (err) {
        setErrors(prev => ({ ...prev, confirmPassword: err.message }));
      }
    }
  };

  const handleSubmit = async () => {
    try {
      await signupSchema.validate(values, { abortEarly: false });

      setIsLoading(true);

      const data = { username: values.username, password: values.password };

      await register(data);

      toast.success(t('registrationComplete'));
      navigate('/login');
    } catch (error) {
      if (error.name === 'ValidationError') {
        console.error('Ошибка валидации:', error.message);
        const validationError = {};

        error.inner.forEach(err => {
          validationError[err.path] = err.message;
        });
        setErrors(validationError);
        return;
      }

      // Backend errors
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;

      // 400: user already exists
      if (detail === errorDetails.REGISTER_USER_ALREADY_EXISTS) {
        createErrorNotification(t('userAlreadyExists'));
        return;
      }

      // 422: validation from backend
      if (status === statusCodes.HTTP_422_UNPROCESSABLE_ENTITY) {
        createErrorNotification(t('validationFailed'));
        return;
      }

      createErrorNotification();
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowPassword = () => setShowPassword(prev => !prev);
  const handleShowConfirmPassword = () => setShowConfirmPassword(prev => !prev);

  const isFormValid =
    Object.values(errors).every(err => !err) &&
    Object.values(values).every(Boolean);

  return (
    <Box width={500} margin={'5% auto 0'}>
      <Typography variant="h1" align="center">
        {title}
      </Typography>

      <Box pt={3}>
        <Paper elevation={3}>
          <Box sx={{ padding: '3rem 2rem 2rem' }}>
            <Box
              component="form"
              autoComplete="off"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: '2.5rem',
              }}
            >
              <FormControl fullWidth variant="standard">
                <InputLabel htmlFor="TextField-1" shrink={false}>
                  {t('username')}
                </InputLabel>
                <TextField
                  id="TextField-1"
                  autoComplete="new-password"
                  autoFocus
                  name="username"
                  value={values.username}
                  onChange={handleChange}
                  error={Boolean(errors.username)}
                  helperText={errors.username}
                />
              </FormControl>

              <FormControl fullWidth variant="standard">
                <InputLabel htmlFor="TextField-2" shrink={false}>
                  {t('password')}
                </InputLabel>
                <TextField
                  id="TextField-2"
                  autoComplete="new-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={values.password}
                  onChange={handleChange}
                  error={Boolean(errors.password)}
                  helperText={errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleShowPassword}
                          edge="end"
                          size="small"
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>

              <FormControl fullWidth variant="standard">
                <InputLabel htmlFor="TextField-3" shrink={false}>
                  {t('confirmPassword')}
                </InputLabel>
                <TextField
                  id="TextField-3"
                  autoComplete="new-password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={values.confirmPassword}
                  onChange={handleChange}
                  error={Boolean(errors.confirmPassword)}
                  helperText={errors.confirmPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleShowConfirmPassword}
                          edge="end"
                          size="small"
                          aria-label="toggle confirm password visibility"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>

              <Button
                type="button"
                variant="contained"
                onClick={handleSubmit}
                disabled={!isFormValid || isLoading}
              >
                {t('signup')}
                {isLoading && (
                  <CircularProgress size={20} color="inherit" sx={{ ml: 1 }} />
                )}
              </Button>

              <Typography variant="h3" sx={{ display: 'flex', gap: '7px' }}>
                {t('haveAccount')}?
                <Link to={'/login'} className="MuiLink">
                  {t('login')}
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default SignUpView;
